import React, { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { CalendarPicker, LocalizationProvider, PickersDay } from '@mui/lab';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Container,
  FormControl,
  Grid,
  Stack,
  TextFieldProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  DatesSetArg,
  EventClickArg,
  EventInput,
  EventSourceInput,
} from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import AdapterMoment from '@mui/lab/AdapterMoment';
import moment, { Moment } from 'moment';
import momentTimezone from 'moment-timezone';

import { countryOptions, TCountryOption } from '../../../../constants';
import {
  getUserAvialabilityDataByUserIdAsAdmin,
  resetAdminUserAvailability,
} from '../../../../store/V2/UserAvailabilitySlice';
import {
  adminGetMeetings,
  getScheduleState,
  getUserSchedulesAsAdmin,
  resetAdminMeetingsData,
  resetAdminUserScheduleData,
  TAdminGetMeetings,
  TAdminGetUserSchedule,
} from '../../../../store/V2/ScheduleSlice';
import {
  getCoursesForAdmin,
  getCoursesState,
  resetAdminCoursesData,
  TGetCoursesForAdmin,
} from '../../../../store/V2/CoursesSlice';
import { getAuthState } from '../../../../store/AuthSlice';
import { useAppSelector } from '../../../../hooks';
import {
  checkUserPermission,
  getFormatedDurationByMinutes,
} from '../../../../helper';
import {
  calendarViewOptions,
  commonConfigs,
  getEventTypeColors,
  TCalendarViewOptions,
} from '../calendarHelper';
import {
  TPAutoCompleteArrowIconComponent,
  TPTextField,
} from '../../../../components/Inputs';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../../components/BreadCrumb/Breadcrumbs';
import CalendarStyles from '../CalendarStyle';
import AvailabilityModal from './modals/AvailabilityModal';
import UserListDropdown, {
  TUserSelectOption,
} from '../../../../components/Inputs/admin/UserListDropdown';
import CalendarHeaderToolBar from './components/CalendarHeaderToolBar';
import ViewScheduleModal from './modals/ViewScheduleModal';
import {
  ROLE_NAMES,
  USER_PERMISSION_INDIVIDUALS,
} from '../../../../store/V2/RolesSlice';
import TimezoneAlertModal from './modals/TimezoneAlertModal';
import {
  getUserByIdAsAdmin,
  getUsersState,
  resetUser,
} from '../../../../store/UserSlice';

type TSelectOption = {
  label: string;
  value: string;
};

type TState = {
  selectedUser: TUserSelectOption | null;
  calendarDate: Moment;
  calendarView: TCalendarViewOptions;
  selectedCourse: TSelectOption | null;
  selectedTimezone: TCountryOption | null;
};

const textFieldProps: TextFieldProps = {
  sx: {
    backgroundColor: '#ffffff',
    '& .MuiInputBase-root': {
      height: '52px',
    },
  },
  InputLabelProps: {
    shrink: true,
  },
};

const defaultTimezoneOption = countryOptions.find(
  (item) => item.value === Intl.DateTimeFormat().resolvedOptions().timeZone
);

const initialState: TState = {
  selectedUser: null,
  calendarDate: moment(),
  calendarView: calendarViewOptions[1],
  selectedCourse: null,
  selectedTimezone: defaultTimezoneOption || null,
};

const AdminSchedulePage: FunctionComponent = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isXsScreen = useMediaQuery(theme.breakpoints.only('xs'));
  const calendarRef = useRef<FullCalendar | null>(null);

  // State
  const [isLoading, setLoading] = useState(false);
  const [state, setState] = useState<TState>({ ...initialState });
  const [timezoneModalData, setTimezoneModalData] = useState<{
    open: boolean;
    selectedTimezone?: string;
    userTimezone?: string;
  }>({ open: false });
  const [isViewAvailabilityModalOpen, setViewAvailabilityModalOpen] = useState(
    false
  );

  const [viewScheduleModalData, setViewScheduleModalData] = useState<{
    open: boolean;
    scheduleEvent?: EventClickArg;
  }>({
    open: false,
  });

  // Store
  const scheduleState = useAppSelector(getScheduleState);
  const coursesState = useAppSelector(getCoursesState);
  const authState = useAppSelector(getAuthState);
  const usersState = useAppSelector(getUsersState);

  const calendarApi = calendarRef.current?.getApi();
  const authUser = authState.user;
  const { adminUserScheduleData, adminMeetingsData } = scheduleState;
  const { adminCoursesData } = coursesState;
  const { user: userData } = usersState;

  const stickyStyles: React.CSSProperties = {};
  const calendarEvents: EventSourceInput = [];
  const coursesOptions: TSelectOption[] = [];

  let selectedUserRemainingTime = '';
  let totalUpcomingClassesCount = 0;

  if (userData) {
    if ('remainingTime' in userData) {
      selectedUserRemainingTime = getFormatedDurationByMinutes({
        minutes: userData.remainingTime || 0,
      });
    }
  }

  if (adminMeetingsData) {
    totalUpcomingClassesCount = adminMeetingsData.totalDocs || 0;
  }

  if (isDesktop) {
    stickyStyles.position = 'sticky';
    stickyStyles.top = '94px';
  }

  if (
    adminUserScheduleData &&
    adminUserScheduleData.list &&
    adminUserScheduleData.list.length
  ) {
    adminUserScheduleData.list.forEach((scheduleItem) => {
      const eventColors = getEventTypeColors(scheduleItem);

      const newCalendarEvent: EventInput = {
        ...eventColors,
        id: scheduleItem._id,
        start: moment(scheduleItem.start).toISOString(),
        end: moment(scheduleItem.end).toISOString(),
        title: scheduleItem.title,
        extendedProps: { ...scheduleItem },
      };

      if (state.selectedTimezone) {
        newCalendarEvent.start = momentTimezone
          .tz(scheduleItem.start, state.selectedTimezone.value)
          .format();

        newCalendarEvent.end = momentTimezone
          .tz(scheduleItem.end, state.selectedTimezone.value)
          .format();
      }

      calendarEvents.push(newCalendarEvent);
    });
  }

  if (
    adminCoursesData &&
    adminCoursesData.list &&
    adminCoursesData.list.length
  ) {
    adminCoursesData.list.forEach((courseItem) => {
      coursesOptions.push({
        label: courseItem.title,
        value: courseItem._id,
      });
    });
  }

  useEffect(() => {
    return () => {
      dispatch(resetAdminUserAvailability());
      dispatch(resetAdminUserScheduleData());
      dispatch(resetAdminCoursesData());
      dispatch(resetUser());
      dispatch(resetAdminMeetingsData());
    };
  }, []);

  useEffect(() => {
    if (isXsScreen && calendarApi) {
      calendarApi.changeView(calendarViewOptions[3].value);

      updateStateAttributes({ calendarView: calendarViewOptions[3] });
    }
  }, [isXsScreen]);

  const updateStateAttributes = (attributes: Partial<TState>) => {
    setState((prevState) => ({
      ...prevState,
      ...attributes,
    }));
  };

  const handleCalendarPickerDateChange = (newDate: Moment | null) => {
    if (newDate && calendarApi) {
      updateStateAttributes({ calendarDate: newDate });

      calendarApi.gotoDate(newDate.toDate());
    }
  };

  const handleUserChange = async (
    event: React.SyntheticEvent<Element, Event>,
    value: TUserSelectOption | null
  ) => {
    setLoading(true);

    dispatch(resetAdminUserScheduleData());
    dispatch(resetAdminUserAvailability());
    dispatch(resetAdminCoursesData());
    dispatch(resetAdminMeetingsData());

    updateStateAttributes({
      selectedUser: value,
      selectedCourse: null,
    });

    if (
      value &&
      state.selectedTimezone &&
      value.country &&
      value.country !== state.selectedTimezone.value
    ) {
      setTimezoneModalData({
        open: true,
        selectedTimezone: state.selectedTimezone.value,
        userTimezone: value.country,
      });
    }
    dispatch(resetUser());
    dispatch(resetAdminUserScheduleData());
    dispatch(resetAdminUserAvailability());
    dispatch(resetAdminCoursesData());

    if (value) {
      dispatch(getUserByIdAsAdmin(value.value));
      // Get user availability slots
      await dispatch(getUserAvialabilityDataByUserIdAsAdmin(value.value));

      // get user courses
      const getCoursesApiData: TGetCoursesForAdmin = {
        limit: 30,
        offset: 0,
      };

      const getMeetingsApiData: TAdminGetMeetings = {
        startDate: moment().toISOString(),
        countOnly: true,
      };

      if (value.roleName === ROLE_NAMES.STUDENT) {
        getCoursesApiData.studentId = value.value;
        getMeetingsApiData.studentId = value.value;
      }

      if (value.roleName === ROLE_NAMES.TUTOR) {
        getCoursesApiData.tutorId = value.value;
        getMeetingsApiData.tutorId = value.value;
      }

      dispatch(adminGetMeetings({ ...getMeetingsApiData }));
      await dispatch(getCoursesForAdmin(getCoursesApiData));

      handleClickToday();
    }

    setLoading(false);
  };

  const handleClickDateNext = () => {
    if (calendarApi) {
      calendarApi.next();
    }
  };

  const handleClickDatePrev = () => {
    if (calendarApi) {
      calendarApi.prev();
    }
  };

  const handleClickToday = () => {
    if (calendarApi) {
      calendarApi.today();
    }
  };

  const handleChangeView = (viewOption: TCalendarViewOptions) => {
    if (calendarApi) {
      calendarApi.changeView(viewOption.value);

      updateStateAttributes({ calendarView: viewOption });
    }
  };

  const handleEventClick = (arg: EventClickArg) => {
    setViewScheduleModalData({
      open: true,
      scheduleEvent: arg,
    });
  };

  const handleDateSet = (arg: DatesSetArg) => {
    const start = moment(arg.start);
    const end = moment(arg.end);

    if (state.selectedUser) {
      const apiData: TAdminGetUserSchedule = {
        userId: state.selectedUser.value,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      };

      if (state.selectedCourse) {
        apiData.courseId = state.selectedCourse.value;
      }

      dispatch(getUserSchedulesAsAdmin(apiData));
    }

    updateStateAttributes({
      calendarDate: moment(arg.start),
    });

    dispatch(resetAdminUserScheduleData());
  };

  const handleCourseChange = (
    event: React.SyntheticEvent,
    value: TSelectOption | null
  ) => {
    updateStateAttributes({
      selectedCourse: value,
    });

    if (state.selectedUser && calendarApi && calendarApi.view) {
      const apiData: TAdminGetUserSchedule = {
        userId: state.selectedUser.value,
        startDate: moment(calendarApi.view.activeStart).toISOString(),
        endDate: moment(calendarApi.view.activeEnd).toISOString(),
      };

      if (value) {
        apiData.courseId = value.value;
      }

      dispatch(getUserSchedulesAsAdmin(apiData));
    }
  };

  const handleTimezoneAlertModalUpdate = (timezone: string) => {
    const matchingTimezone = countryOptions.find(
      (item) => item.value === timezone
    );

    if (matchingTimezone) {
      updateStateAttributes({
        selectedTimezone: matchingTimezone,
      });
    }

    setTimezoneModalData({ open: false });
  };

  const constructBreadcrumbOptions = () => {
    const options: Array<BreadcrumbOption> = [
      {
        type: 'link',
        title: 'Home',
        link: `${process.env.PUBLIC_URL}`,
      },
      {
        type: 'text',
        title: 'Schedule',
        highlight: true,
      },
    ];

    return options;
  };

  const renderWeekPickerDay = (day, selectedDates, pickersDayProps) => {
    if (state.calendarView.value === 'timeGridDay') {
      return <PickersDay {...pickersDayProps} />;
    }

    let start = state.calendarDate.clone().startOf('week');
    let end = state.calendarDate.clone().endOf('week');

    if (state.calendarView.value === 'dayGridMonth') {
      start = state.calendarDate.clone().add(15, 'days').startOf('month');
      end = start.clone().endOf('month');
    }

    const dayIsBetween = day.isSameOrAfter(start) && day.isSameOrBefore(end);
    const isFirstDay = day.isSame(start, 'day');
    const isLastDay = day.isSame(end, 'day');
    const isSelected =
      day.format('YYYY-MM-DD') === moment().format('YYYY-MM-DD');

    const _styles = {
      ...pickersDayProps.style,
      borderRadius: 0,
    };

    if (dayIsBetween) {
      _styles.backgroundColor = '#F1F5F9';
    }

    if (isFirstDay) {
      _styles.borderTopLeftRadius = '50%';
      _styles.borderBottomLeftRadius = '50%';
    }

    if (isLastDay) {
      _styles.borderTopRightRadius = '50%';
      _styles.borderBottomRightRadius = '50%';
    }

    if (isSelected && !dayIsBetween && !isFirstDay && !isLastDay) {
      delete _styles.borderRadius;
    }

    return (
      <PickersDay
        {...pickersDayProps}
        day={day}
        disableMargin
        selected={isSelected}
        sx={_styles}
      />
    );
  };

  const breadcrumbOptions = constructBreadcrumbOptions();

  return (
    <Container maxWidth="xl">
      <div style={{ padding: '30px 0' }}>
        <Breadcrumbs
          options={breadcrumbOptions}
          textColor="#647488"
          showDropDown={isXsScreen}
        />
      </div>
      {checkUserPermission(authUser, {
        adminSchedule: [USER_PERMISSION_INDIVIDUALS.ADMIN_SCHEDULE_EDIT],
      }) ? (
        <Stack direction="row" justifyContent="flex-end" mb="15px">
          <Button
            disabled={!state.selectedUser || isLoading}
            color="inherit"
            variant="text"
            onClick={() => setViewAvailabilityModalOpen(true)}
            sx={{ fontWeight: 600, textDecoration: 'underline' }}
          >
            Set Availability
          </Button>
        </Stack>
      ) : null}
      <div>
        <Grid container rowSpacing="20px" columnSpacing="20px">
          <Grid item style={{ width: isDesktop ? '300px' : '100%' }}>
            <Stack rowGap="20px" flexGrow={1} style={stickyStyles}>
              <FormControl fullWidth>
                <UserListDropdown
                  selectedItem={state.selectedUser}
                  handleChange={handleUserChange}
                  textFieldProps={{
                    label: 'User',
                    placeholder: 'Select',
                    ...textFieldProps,
                  }}
                />
              </FormControl>
              {state.selectedUser ? (
                <Collapse
                  in={Boolean(
                    selectedUserRemainingTime ||
                      typeof totalUpcomingClassesCount === 'number'
                  )}
                >
                  <Card
                    elevation={0}
                    sx={{ backgroundColor: '#F8F9FD', borderRadius: '7px' }}
                  >
                    <CardContent style={{ padding: '8px 16px' }}>
                      <Stack gap="5px">
                        {selectedUserRemainingTime ? (
                          <Typography m={0} fontSize="14px" fontWeight={600}>
                            {`Credits: ${selectedUserRemainingTime}`}
                          </Typography>
                        ) : null}
                        <Typography m={0} fontSize="14px" fontWeight={600}>
                          {`Upcoming classes count: ${totalUpcomingClassesCount}`}
                        </Typography>
                      </Stack>
                    </CardContent>
                  </Card>
                </Collapse>
              ) : null}

              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={state.selectedCourse}
                  options={coursesOptions}
                  disabled={!state.selectedUser}
                  onChange={handleCourseChange}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  loading={isLoading}
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Course"
                      placeholder="Course"
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth>
                <Autocomplete
                  fullWidth
                  size="small"
                  value={state.selectedTimezone}
                  options={countryOptions}
                  popupIcon={TPAutoCompleteArrowIconComponent}
                  onChange={(e, value) =>
                    updateStateAttributes({ selectedTimezone: value })
                  }
                  renderInput={(params) => (
                    <TPTextField
                      {...params}
                      {...textFieldProps}
                      size="small"
                      label="Timezone"
                      placeholder="Select"
                    />
                  )}
                />
              </FormControl>
              {isDesktop ? (
                <Box
                  sx={{
                    backgroundColor: '#ffffff',
                    borderRadius: '20px',
                    boxShadow: '0px 14px 40px 0px #6E9DB22E',
                    '& .MuiCalendarPicker-root': {
                      width: '100%',
                    },
                    '& .css-dhopo2': {
                      minHeight: '200px',
                    },
                  }}
                >
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <CalendarPicker
                      onChange={handleCalendarPickerDateChange}
                      renderDay={renderWeekPickerDay}
                      date={
                        state.calendarView.value === 'dayGridMonth'
                          ? state.calendarDate.clone().add(15, 'days')
                          : state.calendarDate
                      }
                    />
                  </LocalizationProvider>
                </Box>
              ) : null}
            </Stack>
          </Grid>
          <Grid item xs={12} lg>
            <Card
              elevation={0}
              sx={{
                borderRadius: '20px',
                boxShadow: '0px 14px 40px 0px #6E9DB22E',
              }}
            >
              <CardContent sx={{ p: '16px' }}>
                <CalendarStyles>
                  <CalendarHeaderToolBar
                    title={calendarApi?.view.title || ''}
                    view={state.calendarView}
                    onNextDate={handleClickDateNext}
                    onPrevDate={handleClickDatePrev}
                    onToday={handleClickToday}
                    onChangeView={handleChangeView}
                  />
                  <FullCalendar
                    {...commonConfigs}
                    ref={calendarRef}
                    datesSet={handleDateSet}
                    events={calendarEvents}
                    eventClick={handleEventClick}
                    initialView={state.calendarView.value}
                    initialDate={state.calendarDate.toDate()}
                    timeZone={
                      state.selectedTimezone
                        ? state.selectedTimezone.value
                        : 'local'
                    }
                    height={
                      state.calendarView.value === 'dayGridMonth' ||
                      state.calendarView.value === 'listWeek'
                        ? undefined
                        : 'auto'
                    }
                  />
                </CalendarStyles>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </div>
      {/* Modals */}
      {isViewAvailabilityModalOpen && state.selectedUser ? (
        <AvailabilityModal
          open={isViewAvailabilityModalOpen}
          userId={state.selectedUser.value}
          onClose={() => setViewAvailabilityModalOpen(false)}
        />
      ) : null}
      {viewScheduleModalData &&
      viewScheduleModalData.open &&
      viewScheduleModalData.scheduleEvent ? (
        <ViewScheduleModal
          open={viewScheduleModalData.open}
          onClose={() => setViewScheduleModalData({ open: false })}
          sectionId={viewScheduleModalData.scheduleEvent.event.id}
        />
      ) : null}
      {timezoneModalData.open &&
      timezoneModalData.selectedTimezone &&
      timezoneModalData.userTimezone ? (
        <TimezoneAlertModal
          open={timezoneModalData.open}
          userTimezone={timezoneModalData.userTimezone}
          onClose={() => setTimezoneModalData({ open: false })}
          onUpdateTimezone={() =>
            handleTimezoneAlertModalUpdate(timezoneModalData.userTimezone || '')
          }
        />
      ) : null}
    </Container>
  );
};

export default AdminSchedulePage;
